// src/components/modules/devtools/hooks/devIdeSandboxHandlers.js

const createSandboxHandlers = (deps) => {
  const {
    setBackendActionLoading,
    setFrontendActionLoading,
    setActionMessage,
    selectedFileMeta,
    setSaveErrorMessage,
    setSaveErrorOpen,
    // NEW: project scoping
    selectedProjectId,
  } = deps;

  const handleRestartSandboxBackend = async () => {
    setBackendActionLoading(true);
    setActionMessage("");

    try {
      const res = await fetch("/api/dev-ide/restart-sandbox-backend", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: selectedProjectId || null,
        }),
      });

      const data = await res.json().catch(() => null);

      if (res.ok && data && data.success) {
        const exitCode =
          typeof data.exit_code === "number" ? data.exit_code : 0;
        setActionMessage(
          `Sandbox Flask backend restart triggered (exit code ${exitCode}).`
        );
      } else {
        const errorText =
          (data && (data.error || data.error_output)) ||
          "Unknown error restarting backend.";
        setActionMessage(
          `Backend restart may have partially succeeded. Details: ${errorText}`
        );
      }
    } catch (err) {
      setActionMessage(
        `Error calling backend restart: ${err.message || String(err)}`
      );
    } finally {
      setBackendActionLoading(false);
    }
  };

  const handleRebuildSandboxFrontend = async () => {
    setFrontendActionLoading(true);
    setActionMessage("");
    try {
      const res = await fetch("/api/dev-ide/rebuild-sandbox-frontend", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: selectedProjectId || null,
        }),
      });
      const data = await res.json().catch(() => null);

      if (res.ok && data && data.success) {
        const exitCode =
          typeof data.exit_code === "number" ? data.exit_code : 0;
        setActionMessage(
          `Sandbox frontend rebuild triggered (exit code ${exitCode}). Check logs for build output.`
        );
      } else {
        const errorText =
          (data && (data.error || data.error_output)) ||
          "Unknown error rebuilding frontend.";
        setActionMessage(`Error rebuilding sandbox frontend: ${errorText}`);

        // NEW: trigger global error popup so rebuild failures can't be missed
        if (setSaveErrorMessage && setSaveErrorOpen) {
          setSaveErrorMessage(`Frontend rebuild failed: ${errorText}`);
          setSaveErrorOpen(true);
        }
      }
    } catch (err) {
      setActionMessage(
        `Error rebuilding sandbox frontend: ${err.message || String(err)}`
      );

      // NEW: popup on unexpected exception during rebuild call
      if (setSaveErrorMessage && setSaveErrorOpen) {
        setSaveErrorMessage(`Frontend rebuild failed: ${err.message}`);
        setSaveErrorOpen(true);
      }
    } finally {
      setFrontendActionLoading(false);
    }
  };

  const handleRunBackendScan = async () => {
    const params = new URLSearchParams();
    if (selectedProjectId) {
      params.set("project_id", selectedProjectId);
    }

    await fetch(
      `/api/dev-ide/run-backend-scan${
        params.toString() ? `?${params.toString()}` : ""
      }`,
      { method: "POST" }
    );
  };

  const handleRunFrontendScan = async () => {
    const params = new URLSearchParams();
    if (selectedProjectId) {
      params.set("project_id", selectedProjectId);
    }

    await fetch(
      `/api/dev-ide/run-frontend-scan${
        params.toString() ? `?${params.toString()}` : ""
      }`,
      { method: "POST" }
    );
  };

  const handleDescribeFile = async () => {
    if (!selectedFileMeta?.path) return;

    await fetch("/api/dev-ide/describe-file", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        path: selectedFileMeta.path,
        project_id: selectedProjectId || null,
      }),
    });
  };

  return {
    handleRestartSandboxBackend,
    handleRebuildSandboxFrontend,
    handleRunBackendScan,
    handleRunFrontendScan,
    handleDescribeFile,
  };
};

export default createSandboxHandlers;